import React from 'react';
import { useNavigate } from 'react-router-dom';
import './CookiePolicyPage.css';

const CookiePolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="policy-page">
      <div className="policy-hero">
        <div className="container">
          <button className="back-btn" onClick={() => navigate('/')}>
            ← Back to Home
          </button>
          <h1>Cookie Policy</h1>
          <p className="last-updated">Last Updated: February 27, 2026</p>
        </div>
      </div>

      <div className="policy-content">
        <div className="container">
          <div className="policy-section">
            <h2>1. What Are Cookies</h2>
            <p>
              Cookies are small text files that are placed on your computer or mobile device when you visit a website. 
              They are widely used to make websites work more efficiently and provide information to website owners. 
              Cookies help us understand how you use our website and improve your experience.
            </p>
          </div>

          <div className="policy-section">
            <h2>2. How We Use Cookies</h2>
            <p>
              DT Consultancy uses cookies for various purposes:
            </p>
            <ul>
              <li><strong>Essential Cookies:</strong> These are necessary for our website to function properly and cannot be disabled.</li>
              <li><strong>Performance Cookies:</strong> These help us understand how visitors interact with our website by collecting and reporting information anonymously.</li>
              <li><strong>Functionality Cookies:</strong> These enable the website to provide enhanced functionality and personalization.</li>
              <li><strong>Targeting/Advertising Cookies:</strong> These may be set through our site by our advertising partners to build a profile of your interests.</li>
            </ul>
          </div>

          <div className="policy-section">
            <h2>3. Types of Cookies We Use</h2>
            
            <div className="cookie-type">
              <h3>Session Cookies</h3>
              <p>
                These are temporary cookies that expire when you close your browser. They help us maintain your 
                session as you navigate through our website.
              </p>
            </div>

            <div className="cookie-type">
              <h3>Persistent Cookies</h3>
              <p>
                These remain on your device for a set period of time or until you delete them. They help us 
                recognize you when you return to our website and remember your preferences.
              </p>
            </div>

            <div className="cookie-type">
              <h3>First-Party Cookies</h3>
              <p>
                These are set by DT Consultancy directly and are used to provide you with a better browsing 
                experience and enable certain features.
              </p>
            </div>

            <div className="cookie-type">
              <h3>Third-Party Cookies</h3>
              <p>
                These are set by third-party services we use, such as Google Analytics, to help us analyze 
                how our website is used and to deliver targeted advertising.
              </p>
            </div>
          </div>

          <div className="policy-section">
            <h2>4. Specific Cookies We Use</h2>
            
            <table className="cookie-table">
              <thead>
                <tr>
                  <th>Cookie Name</th>
                  <th>Purpose</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>session_id</td>
                  <td>Maintains your session state</td>
                  <td>Session</td>
                </tr>
                <tr>
                  <td>user_preferences</td>
                  <td>Stores your website preferences</td>
                  <td>1 year</td>
                </tr>
                <tr>
                  <td>_ga</td>
                  <td>Google Analytics - distinguishes users</td>
                  <td>2 years</td>
                </tr>
                <tr>
                  <td>_gid</td>
                  <td>Google Analytics - distinguishes users</td>
                  <td>24 hours</td>
                </tr>
                <tr>
                  <td>consent</td>
                  <td>Stores your cookie consent preferences</td>
                  <td>1 year</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="policy-section">
            <h2>5. Third-Party Services</h2>
            <p>
              We use several third-party services that may set cookies:
            </p>
            <ul>
              <li><strong>Google Analytics:</strong> To analyze website traffic and user behavior</li>
              <li><strong>Google Ads:</strong> To deliver relevant advertisements</li>
              <li><strong>LinkedIn:</strong> For professional networking and analytics</li>
              <li><strong>Social Media Platforms:</strong> To enable sharing and social features</li>
            </ul>
            <p>
              These third parties have their own privacy policies and cookie policies, which we encourage you to review.
            </p>
          </div>

          <div className="policy-section">
            <h2>6. Managing Cookies</h2>
            <p>
              You have the right to decide whether to accept or reject cookies. You can manage your cookie preferences 
              through:
            </p>
            
            <div className="cookie-type">
              <h3>Browser Settings</h3>
              <p>
                Most web browsers allow you to control cookies through their settings. You can set your browser to 
                refuse all or some browser cookies, or to alert you when websites set or access cookies.
              </p>
              <ul>
                <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies and other site data</li>
                <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies and Site Data</li>
                <li><strong>Safari:</strong> Preferences → Privacy → Cookies and website data</li>
                <li><strong>Edge:</strong> Settings → Cookies and site permissions → Manage and delete cookies</li>
              </ul>
            </div>

            <div className="cookie-type">
              <h3>Cookie Consent Banner</h3>
              <p>
                When you first visit our website, you'll see a cookie consent banner where you can choose which 
                types of cookies to accept. You can update your preferences at any time.
              </p>
            </div>
          </div>

          <div className="policy-section">
            <h2>7. Impact of Disabling Cookies</h2>
            <p>
              If you choose to disable cookies, some features of our website may not function properly. This could include:
            </p>
            <ul>
              <li>Inability to remember your preferences</li>
              <li>Difficulty logging in or maintaining your session</li>
              <li>Reduced website functionality</li>
              <li>Less personalized content and advertising</li>
            </ul>
          </div>

          <div className="policy-section">
            <h2>8. Do Not Track Signals</h2>
            <p>
              Some browsers have a "Do Not Track" feature that lets you tell websites you do not want to have your 
              online activities tracked. Currently, our website does not respond to Do Not Track signals.
            </p>
          </div>

          <div className="policy-section">
            <h2>9. Updates to This Policy</h2>
            <p>
              We may update this Cookie Policy from time to time to reflect changes in technology, legislation, or 
              our business operations. We will notify you of any significant changes by posting the updated policy 
              on our website.
            </p>
          </div>

          <div className="policy-section">
            <h2>10. Contact Us</h2>
            <p>
              If you have any questions about our use of cookies, please contact us:
            </p>
            <ul>
              <li>By email: <a href="mailto:dt.consult.common@gmail.com">dt.consult.common@gmail.com</a></li>
              <li>By phone: <a href="tel:+918439533217">+91-8439533217</a></li>
              <li>By mail: D-9, Ground Floor, Sec-3, Noida, UP 201301, India</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicyPage;
